import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '../buttons/Button';

import styles from '../../../styles/partials/results-question.module.css';

interface ResultsQuestionProps {
    question: {
        label: string;
        body: string;
        validated: boolean;
        valid: boolean;
        points: number;
        answerDuration: string;
        explanation: string;
    };
    open?: boolean;
    number?: string;
    withExplanation?: boolean;
    itemIsValid?: boolean;
}

function ResultsQuestion({
    open = false,
    question = null,
    number = null,
    withExplanation = false,
    itemIsValid = false,
}: ResultsQuestionProps) {
    const {
        label = null,
        body = null,
        validated = false,
        valid = false,
        points = null,
        answerDuration = null,
        explanation = null,
    } = question || {};

    const [expanded, setExpanded] = useState(open);

    const onExpandClicked = useCallback(() => setExpanded((value) => !value), [setExpanded]);

    return (
        <div
            className={classNames(styles.container, {
                [styles.expanded]: expanded,
                [styles.collapsed]: !expanded,
                [styles.disabled]: !validated,
            })}
        >
            {number !== null ? <div className={styles.number}>{number}</div> : null}
            {label !== null ? <div className={styles.label}>{label}</div> : null}
            <div
                className={classNames(styles.tick, {
                    [styles.valid]: validated && valid,
                    [styles.invalid]: validated && !valid,
                })}
            />
            <div className={styles.body}>
                {validated ? <div>{body}</div> : <div className={styles.placeholder} />}
                <div className={classNames(styles.stats, styles.expandable)}>
                    {answerDuration !== null && parseInt(answerDuration, 10) > 0 ? (
                        <div className={styles.time}>
                            <span className={styles.icon} />
                            <span>{answerDuration}</span>
                        </div>
                    ) : null}
                    {points !== null ? (
                        <div className={styles.points}>
                            {validated && valid && points !== null ? points : 0} pts
                        </div>
                    ) : null}
                </div>
                {validated ? (
                    <div
                        className={classNames(styles.status, styles.expandable, {
                            [styles.withoutGap]: !itemIsValid,
                        })}
                    >
                        <span
                            className={classNames({
                                [styles.valid]: valid && itemIsValid,
                                [styles.invalid]: !valid && itemIsValid,
                            })}
                        />
                        <span>
                            {valid ? (
                                <FormattedMessage
                                    defaultMessage="Correct answer"
                                    description="Rhone Academy - Results - Correct"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Wrong answer"
                                    description="Rhone Academy - Results - Incorrect"
                                />
                            )}
                        </span>
                    </div>
                ) : null}
                {validated && withExplanation && explanation !== null ? (
                    <div className={styles.explanation}>
                        <div className={styles.explanationBody}>{explanation}</div>
                    </div>
                ) : null}
            </div>
            <Button
                type="button"
                className={styles.expandButton}
                onClick={onExpandClicked}
                aria-label="Expand"
                disabled={!validated}
            >
                <span className={styles.icon} />
            </Button>
        </div>
    );
}

export default ResultsQuestion;
